import { intl } from "LocaleWrapper";
import { Card } from "@cofano/cck";
import Description from "@mui/icons-material/Description";
import Documents, { DocumentOptionalColumns, Entity } from "components/documents/Documents";
import { getDocumentsCardTitle } from "components/documents/DocumentsUtils";
import { actionTypeToDocumentType } from "view/voyages/utils/VoyageUtils";
import { DocumentEntity } from "__generated__/enums";
import { VoyageDocumentsVoyageFragment } from "__generated__/view/voyages/graphql/voyage.fragments.generated";

interface VoyageDocumentsProps {
	voyage: VoyageDocumentsVoyageFragment;
	entities?: [Entity, ...Entity[]];
	showActions?: boolean;
	setSelection?: (selection: { id?: number }[]) => void;
	refetch?: () => void;
	warning?: string;
	collapsable?: boolean;
	subtitle?: string;
}

const getDefaultEntities = (voyage: VoyageDocumentsProps["voyage"]): [Entity, ...Entity[]] => {
	const voyageEntity: Entity = {
		id: voyage.id,
		name: voyage.voyageNumber,
		type: DocumentEntity.Voyage,
	};

	const callEntities: Entity[] = voyage.calls.map(call => ({
		id: call.id,
		name: `${call.location?.description} - ${voyage.voyageNumber}`,
		type: DocumentEntity.Call,
	}));

	const cargoEntities: Entity[] = voyage.cargos.map(cargo => ({
		id: cargo.id,
		name: cargo.cargoNumber ?? cargo.containerNumber ?? cargo.product?.name ?? intl.formatMessage({ id: "CARGO" }),
		type: DocumentEntity.Cargo,
	}));

	const cargoActionEntities: Entity[] = voyage.calls.flatMap(call =>
		call.cargos.map(action => ({
			id: action.cargo.id,
			cargoActionId: action.id,
			name: `${action.location?.description} - ${action.location?.displayCode || action.location?.code || ""} (${action.type && (intl.formatMessage({ id: action.type }))})`,
			type: action.type && actionTypeToDocumentType(action.type) || DocumentEntity.LoadingCargo,
			callId: call.id,
		})),
	);

	return [ voyageEntity, ...cargoEntities, ...callEntities, ...cargoActionEntities ];
};

const VoyageDocuments = ({ voyage, entities, showActions, setSelection, refetch, warning, collapsable, subtitle }: VoyageDocumentsProps) => {
	const resolvedEntities = entities ?? getDefaultEntities(voyage);

	return (
		<div>
			<Card
				title={getDocumentsCardTitle(resolvedEntities)}
				subtitle={subtitle}
				icon={<Description fontSize="small" color="primary" />}
				collapsable={collapsable}
				disablePadding>
				<Documents
					entities={resolvedEntities}
					showOptionalColumns={[ DocumentOptionalColumns.Entity ]}
					showActions={showActions}
					setSelection={setSelection}
					onRefetch={refetch}
					warning={warning} />
			</Card>
		</div>
	);
};

export default VoyageDocuments;
